/**
 * BaseSignalRClient
 * Abstract base class for all SignalR hub connections
 * Provides common functionality for connection management, event handling, and reconnection
 */
import * as signalR from '@microsoft/signalr';
import { ConnectionState, DEFAULT_SIGNALR_OPTIONS, HubInvocationError, ConnectionError, } from './types';
/**
 * Abstract base class for SignalR hub clients
 */
export class BaseSignalRClient {
    constructor(hubUrl, getToken, options = {}) {
        this.hubUrl = hubUrl;
        this.getToken = getToken;
        this.connection = null;
        this.connectionState = ConnectionState.Disconnected;
        this.eventHandlers = new Map();
        this.stateChangeListeners = new Set();
        this.reconnectAttempts = 0;
        this.connectionPromise = null;
        this.options = { ...DEFAULT_SIGNALR_OPTIONS, ...options };
    }
    /**
     * Get current connection state
     */
    getState() {
        return this.connectionState;
    }
    /**
     * Check if connected
     */
    isConnected() {
        return this.connectionState === ConnectionState.Connected;
    }
    /**
     * Subscribe to connection state changes
     */
    onStateChange(listener) {
        this.stateChangeListeners.add(listener);
        return () => this.stateChangeListeners.delete(listener);
    }
    /**
     * Connect to the SignalR hub
     */
    async connect() {
        if (this.connectionState === ConnectionState.Connected) {
            return;
        }
        if (this.connectionState === ConnectionState.Connecting && this.connectionPromise) {
            return this.connectionPromise;
        }
        this.connectionPromise = this.doConnect();
        return this.connectionPromise;
    }
    /**
     * Disconnect from the SignalR hub
     */
    async disconnect() {
        if (!this.connection) {
            return;
        }
        try {
            await this.connection.stop();
        }
        catch (error) {
            console.warn('[BaseSignalRClient] Error during disconnect:', error);
        }
        finally {
            this.connection = null;
            this.setConnectionState(ConnectionState.Disconnected);
            this.connectionPromise = null;
        }
    }
    /**
     * Subscribe to a server event
     */
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, new Set());
        }
        this.eventHandlers.get(event).add(handler);
        return () => this.off(event, handler);
    }
    /**
     * Unsubscribe from a server event
     */
    off(event, handler) {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.eventHandlers.delete(event);
            }
        }
    }
    /**
     * Subscribe to an event once
     */
    once(event, handler) {
        const wrappedHandler = (data) => {
            this.off(event, wrappedHandler);
            handler(data);
        };
        return this.on(event, wrappedHandler);
    }
    /**
     * Invoke a hub method
     */
    async invoke(methodName, ...args) {
        if (!this.connection || this.connectionState !== ConnectionState.Connected) {
            const errorMsg = `Cannot invoke ${methodName}: not connected to hub (state: ${this.connectionState})`;
            console.error('[BaseSignalRClient]', errorMsg);
            throw new Error(errorMsg);
        }
        // Additional check for SignalR connection state
        const signalRState = this.connection.state;
        if (signalRState !== signalR.HubConnectionState.Connected) {
            const errorMsg = `Cannot invoke ${methodName}: SignalR connection not ready (state: ${signalRState})`;
            console.error('[BaseSignalRClient]', errorMsg);
            throw new Error(errorMsg);
        }
        try {
            console.log(`[BaseSignalRClient] Invoking hub method: ${methodName}`, args);
            const result = await this.connection.invoke(methodName, ...args);
            console.log(`[BaseSignalRClient] Hub method ${methodName} completed successfully`);
            return result;
        }
        catch (error) {
            // Check if this is a permission/authorization error (expected for certain methods)
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isForbiddenError = errorMessage.includes('Forbidden') ||
                errorMessage.includes('Admin role required') ||
                errorMessage.includes('Unauthorized');
            // Log as debug for expected permission errors, error for unexpected failures
            if (isForbiddenError) {
                console.debug(`[BaseSignalRClient] Hub method ${methodName} forbidden (expected for non-admin):`, errorMessage);
            }
            else {
                console.error(`[BaseSignalRClient] Hub method ${methodName} failed:`, error);
            }
            throw new HubInvocationError(`Failed to invoke hub method: ${methodName}`, methodName, error);
        }
    }
    /**
     * Send a hub method without waiting for response
     */
    async send(methodName, ...args) {
        if (!this.connection || this.connectionState !== ConnectionState.Connected) {
            throw new Error(`Cannot send ${methodName}: not connected to hub`);
        }
        try {
            await this.connection.send(methodName, ...args);
        }
        catch (error) {
            throw new HubInvocationError(`Failed to send to hub method: ${methodName}`, methodName, error);
        }
    }
    /**
     * Emit event to local event handlers
     */
    emit(event, data) {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.forEach((handler) => handler(data));
        }
    }
    /**
     * Perform the actual connection
     */
    async doConnect() {
        this.setConnectionState(ConnectionState.Connecting);
        try {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(this.hubUrl, {
                accessTokenFactory: async () => {
                    try {
                        const token = await this.getToken();
                        if (!token || token.trim() === '') {
                            console.warn('[SignalR] Token unavailable');
                            return '';
                        }
                        return token;
                    }
                    catch (error) {
                        console.error('[SignalR] Token factory error:', error);
                        return '';
                    }
                },
                // Use multiple transports with fallback (matches working test app)
                transport: signalR.HttpTransportType.WebSockets |
                    signalR.HttpTransportType.ServerSentEvents |
                    signalR.HttpTransportType.LongPolling,
                timeout: this.options.connectionTimeout,
                withCredentials: false,
            })
                .withAutomaticReconnect(this.createReconnectPolicy())
                .configureLogging(this.options.logLevel)
                .build();
            this.setupConnectionHandlers();
            this.setupServerMethods();
            await this.connection.start();
            this.reconnectAttempts = 0;
            this.setConnectionState(ConnectionState.Connected);
        }
        catch (error) {
            this.setConnectionState(ConnectionState.Disconnected, error);
            throw new ConnectionError(`Failed to connect to hub: ${this.hubUrl}`, this.hubUrl, error);
        }
        finally {
            this.connectionPromise = null;
        }
    }
    /**
     * Setup connection event handlers
     */
    setupConnectionHandlers() {
        if (!this.connection)
            return;
        this.connection.onclose((error) => {
            this.setConnectionState(ConnectionState.Disconnected, error);
        });
        this.connection.onreconnecting((error) => {
            this.setConnectionState(ConnectionState.Reconnecting, error);
            this.reconnectAttempts++;
        });
        this.connection.onreconnected(() => {
            this.reconnectAttempts = 0;
            this.setConnectionState(ConnectionState.Connected);
        });
    }
    /**
     * Create reconnection policy with exponential backoff
     */
    createReconnectPolicy() {
        const delays = this.options.reconnectDelays;
        return {
            nextRetryDelayInMilliseconds: (retryContext) => {
                if (this.options.maxReconnectAttempts !== Infinity &&
                    retryContext.previousRetryCount >= this.options.maxReconnectAttempts) {
                    return null; // Stop reconnecting
                }
                // Use predefined delays or calculate exponential backoff
                if (retryContext.previousRetryCount < delays.length) {
                    return delays[retryContext.previousRetryCount];
                }
                // Max delay reached, use last delay value
                return delays[delays.length - 1];
            },
        };
    }
    /**
     * Set connection state and notify listeners
     */
    setConnectionState(state, error) {
        this.connectionState = state;
        this.stateChangeListeners.forEach((listener) => listener(state, error));
    }
    /**
     * Cleanup resources
     */
    async destroy() {
        await this.disconnect();
        this.eventHandlers.clear();
        this.stateChangeListeners.clear();
    }
}
//# sourceMappingURL=BaseSignalRClient.js.map